// Das Boot schaukelt mit den Wellen (Erste Fassung, Runde 8; Fabi 20.10.2026: „nicht nur vorne hinten wie in
// Stop-Motion, sondern auch ein wenig links rechts“): heben, vor und zurück und neigen, nach den Hinweisen „Schaukeln“ in
// public/art/fahrt/fahrt-spiel.json des Grafik-Leads. Alles in ganzen Pixeln, damit nichts zittert: Das Boot wird in
// Streifen gleichen Versatzes zerlegt, jedes Tier, jede Laterne und der Rauch rücken als Ganzes mit dem Punkt des Boots,
// auf dem sie stehen. Reine Rechnung, ohne DOM, damit sie in Tests läuft.

/** Schaukeln einer Sturmstufe (sturmstufen[id].schaukeln) */
export type Schaukeln = { hub: number; neigung: number; schub: number; tempo: number };
/** Grenzen der Neigung je Boot und Stärke (schaukeln.grenzen[art][n]) */
export type Grenzen = Record<string, { rechts: number[]; links: number[] }>;

/** Heben (plus = tiefer), vor und zurück (plus = nach rechts) und Neigung (plus = Bug hoch), ganze Pixel */
export type Sway = { h: number; s: number; q: number };

/** Ein Boot, wie das Schaukeln es braucht: Breite, Höhe, Wasserlinie im Bild, Heck und Bug */
export type Hull = { w: number; h: number; wl: number; heck: number; bug: number };

/** Die Rechnung ist auf Zahlen bis 3 gemacht; mit den Werten der Sturmstufen kommt mehr nicht vor */
const MAX_TILT = 3;

/** Wie das Boot bei Phase P liegt: drei Wellen verschiedener Länge, Heben und Vor-zurück um eine Viertelwelle versetzt */
export function swayAt(P: number, k: Schaukeln): Sway {
  const a1 = P;
  const a2 = 1.53 * P + 1.1;
  const a3 = 0.61 * P + 2.0;
  return {
    h: Math.round(k.hub * (0.62 * Math.sin(a1) + 0.3 * Math.sin(a2) + 0.2 * Math.sin(a3))) || 0,
    s: Math.round(k.schub * (0.62 * Math.cos(a1) + 0.3 * Math.cos(a2) + 0.2 * Math.cos(a3))) || 0,
    q: Math.max(-MAX_TILT, Math.min(MAX_TILT, Math.round(k.neigung * (0.6 * Math.cos(a1 + 0.5) + 0.3 * Math.sin(1.93 * P + 0.4) + 0.25 * Math.sin(0.77 * P + 2.6))))) || 0,
  };
}

/** Versatz eines Punkts im Bild des Boots: dy hängt von der Spalte ab, dx von der Zeile */
export type Rock = Sway & {
  dx: (y: number) => number;
  dy: (x: number) => number;
  /** Spaltenstreifen [x0, x1) gleichen dy und Zeilenstreifen [y0, y1) gleichen dx */
  cols: [number, number, number][];
  rows: [number, number, number][];
};

/** Der Versatz für ein Boot (Hinweis „Schaukeln, Versatz eines Punkts“) */
export function rockOf(sway: Sway, hull: Hull, grenzen: Grenzen | undefined): Rock {
  const { h, s, q } = sway;
  const halb = Math.max(20, (hull.bug - hull.heck) / 2);
  const sign = Math.sign(q);
  const g = q !== 0 ? grenzen?.[String(Math.abs(q))] : undefined;
  // Ohne Grenzen für dieses Boot: gleichmäßig zwischen Mitte und Bug bzw. Heck verteilt
  const n = Math.abs(q);
  const xm = (hull.heck + hull.bug) / 2;
  const rechts = g?.rechts ?? Array.from({ length: n }, (_, i) => Math.round(xm + ((hull.bug - xm) * (i + 1)) / (n + 1)));
  const links = g?.links ?? Array.from({ length: n }, (_, i) => Math.round(xm - ((xm - hull.heck) * (i + 1)) / (n + 1)));
  const dy = (x: number) => {
    let tilt = 0;
    for (const b of rechts) if (x >= b) tilt -= sign;
    for (const b of links) if (x < b) tilt += sign;
    return h + tilt || 0;
  };
  const dx = (y: number) => s - Math.round((q * (hull.wl - y)) / halb) || 0;
  // Spalten: an jeder Grenze kann sich dy ändern
  const cuts = [...new Set([0, hull.w, ...rechts, ...links].filter((x) => x >= 0 && x <= hull.w))].sort((a, b) => a - b);
  const cols: [number, number, number][] = [];
  for (let i = 0; i + 1 < cuts.length; i++) {
    const [x0, x1] = [cuts[i]!, cuts[i + 1]!];
    if (x1 > x0) merge(cols, x0, x1, dy(x0));
  }
  // Zeilen: dx ändert sich, wo die Rundung springt
  const rows: [number, number, number][] = [];
  for (let y = 0; y < hull.h; y++) merge(rows, y, y + 1, dx(y));
  return { h, s, q, dx, dy, cols, rows };
}

/** Streifen anhängen; gleicher Versatz wie der vorige wird zusammengelegt */
function merge(list: [number, number, number][], a: number, b: number, v: number): void {
  const last = list[list.length - 1];
  if (last && last[2] === v && last[1] === a) last[1] = b;
  else list.push([a, b, v]);
}

/** Ruhig liegendes Boot */
export const STILL: Sway = { h: 0, s: 0, q: 0 };
